import { Badge, Box, Divider, IconButton, Popover, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { selectNotifications } from "../../redux/selectors/firebase";
import { Notifications as NotificationsIcon, NotificationsNone, Done, DoneAll, ClearAll, RemoveDone, Clear } from "@mui/icons-material"
import CommentReplyNotification, { CommentReplyDetails } from "./CommentReplyNotification";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import { selectUserId } from "../../redux/selectors/editor";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles((theme) => ({
    actions: {
        display: "flex",
        justifyContent: "flex-end",
        margin: theme.spacing(1)
    },
    empty: {
        margin: theme.spacing(1)
    },
    secondary: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
    },
    content: {
        width: "350px"
    }
}));

export interface TaggedInCommentDetails
{
    type: "commentTag"
    commentId: string
    postedByUserId: string
    project: string
}

export type NotificationDetails = CommentReplyDetails | TaggedInCommentDetails;

export interface INotification
{
    read?: boolean
    timestamp: number
    details: NotificationDetails
}

export interface Notifications
{
    [userId: string]: INotification;
}

interface Props
{
    notifications: Notifications
    userId: string
}

function NotificationsList({ notifications, userId }: Props)
{
    const { track, untrack, remove, set } = useContext(FirebaseContext);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const classes = useStyles();

    const detailsComponent: { [type: string]: React.FunctionComponent<any> } = {
        default: () => <Typography>Unknown Notification</Typography>,
        commentReply: CommentReplyNotification
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    {
        setAnchorEl(event.currentTarget);
    }

    const markAsRead = (id: string, read: boolean) =>
    {
        set(`notifications/${userId}/${id}/read`, read);
    }

    const handleMarkAsReadClick = (event: React.MouseEvent<HTMLElement>) =>
    {
        const id = event.currentTarget.dataset.id;
        const read = event.currentTarget.dataset.read === "true";
        if (id)
        {
            markAsRead(id, !read);
        }
    }

    const handleMarkAllAsReadClick = (event: React.MouseEvent<HTMLElement>) =>
    {
        Object.keys(notifications ?? {}).forEach((id) =>
        {
            markAsRead(id, true);
        });
    }

    const handleClearNotificationsClick = (event: React.MouseEvent<HTMLElement>) =>
    {
        const id = event.currentTarget.dataset.id;
        if (id)
        {
            remove(`notifications/${userId}/${id}`);
        }
    }

    const handleClearAllNotificationsClick = (event: React.MouseEvent<HTMLElement>) =>
    {
        remove(`notifications/${userId}`);
    }

    const open = Boolean(anchorEl);

    useEffect(() =>
    {
        const path = `notifications/${userId}/`;
        if (!notifications && userId)
        {
            track(path, ["notifications"]);
        }
    }, [track, untrack, userId, notifications]);
    const unreadNotificationsCount = Object.values(notifications ?? {}).filter((notification) => !notification.read).length;
    return <>
        <IconButton onClick={handleClick} color={"inherit"}>
            <Badge badgeContent={unreadNotificationsCount} color="error" >
                {notifications ? <NotificationsIcon /> : <NotificationsNone />}
            </Badge >
        </IconButton>
        <Popover open={open}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            onClose={() => { setAnchorEl(null) }}>
            {notifications ?
                <Box className={classes.content}>
                    {Object.entries(notifications)
                        .sort(([idA, notificationA], [idB, notificationB]) => { return notificationB.timestamp - notificationA.timestamp; })
                        .map(([id, notification]) => (<Box key={id}>
                            {React.createElement(detailsComponent[notification.details.type] ?? detailsComponent.default, {
                                ...notification, id, markAsRead,
                                children: <>
                                    <IconButton title="Clear notification" data-id={id} onClick={handleClearNotificationsClick}>
                                        <Clear />
                                    </IconButton>
                                    <IconButton title="Mark as read" data-id={id} data-read={notification.read} onClick={handleMarkAsReadClick}>
                                        {notification.read ? <RemoveDone /> : <Done />}
                                    </IconButton>
                                </>
                            })}
                            <Divider />
                        </Box>))}
                    <Box className={classes.actions}>
                        <IconButton title="Clear all notifications" onClick={handleClearAllNotificationsClick} disabled={notifications === undefined}>
                            <ClearAll />
                        </IconButton>
                        <IconButton title="Mark all as read" onClick={handleMarkAllAsReadClick} disabled={notifications === undefined}>
                            <DoneAll />
                        </IconButton>
                    </Box>
                </Box> :
                <Box className={classes.empty}>
                    <Typography>You have no notifications.</Typography>
                </Box>}

        </Popover>
    </>
}

const mapStateToProps = (store: never) => ({
    notifications: selectNotifications(store),
    userId: selectUserId(store)
});
export default connect(mapStateToProps, {
})(NotificationsList);